import * as z from 'zod/v4'
import {getFieldCopy} from '@elanco/forms'
import type {LocaleKey, FieldKey} from '@elanco/forms'
import {logError} from '@/services/logger/client'
import {ROLES} from '../data'
import type {FormField} from './model'

type RoleOption = (typeof ROLES)[number]

const getRoleCodenames = (roleSelections: unknown[] | undefined): string[] => {
	if (!Array.isArray(roleSelections)) {
		return []
	}

	const roleCodenames = roleSelections.flatMap((role) => {
		if (
			typeof role !== 'object' ||
			role === null ||
			typeof (role as {codename?: unknown}).codename !== 'string'
		) {
			return []
		}

		return [(role as {codename: string}).codename]
	})

	return [...new Set(roleCodenames)]
}

const getRoleOptionsByCodename = (roleCodenames: string[]): RoleOption[] => {
	const configuredRoleCodenames = new Set(roleCodenames)

	return ROLES.filter((role) => configuredRoleCodenames.has(role.id))
}

export const getConfiguredRoleOptions = (field: FormField): RoleOption[] => {
	const explicitRoleOptions = getRoleCodenames(
		field.elements?.formCdcRoleFieldOptions
	)

	if (explicitRoleOptions.length > 0) {
		return getRoleOptionsByCodename(explicitRoleOptions)
	}

	const configuredRoleCodenames = [
		...getRoleCodenames(
			field.elements?.formCdcRoleFieldRolesRequiringProfessionalNumber
		),
		...getRoleCodenames(field.elements?.formCdcRoleFieldOtherRoles),
	]

	if (configuredRoleCodenames.length === 0) {
		return ROLES
	}

	return getRoleOptionsByCodename(configuredRoleCodenames)
}

const getConfiguredRoleValues = (field: FormField): string[] => {
	return getConfiguredRoleOptions(field).map((role) => role.value)
}

export const getProfessionalNumberRoleValues = (field: FormField): string[] => {
	return getRoleOptionsByCodename(
		getRoleCodenames(
			field.elements?.formCdcRoleFieldRolesRequiringProfessionalNumber
		)
	).map((role) => role.value)
}

// Helper function to safely get field error messages
export const getFieldError = (
	locale: LocaleKey,
	field: FieldKey,
	errorType: string
): string | undefined => {
	const fieldCopy = getFieldCopy(locale, field)
	// eslint-disable-next-line @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-return, @typescript-eslint/no-explicit-any -- accessing dynamic error properties from field union type
	return (fieldCopy as any)?.errors?.[errorType]
}

// Type for form generation result
interface FormGenerationResult<T> {
	data: T
	error?: {
		message: string
		fieldType: string
		fieldCodename: string
		fieldId: string
	}
}

/**
 * Convert form field type to field name
 * @param type - The field type (e.g., 'form_select_field_name')
 * @returns The camelCase field name (e.g., 'fieldName')
 */
export const convertTypeToFieldName = (type: string): string | undefined => {
	return FIELD_NAME_MAPPING[type]
}

/**
 * Check if a form field is required based on its elements
 */
const isFieldRequired = (field: FormField): boolean => {
	if (!field.elements) return false

	return (
		// CDC Fields
		field.elements.formCdcFirstNameFieldRequired?.[0]?.codename === 'yes' ||
		field.elements.formCdcLastNameFieldRequired?.[0]?.codename === 'yes' ||
		field.elements.formCdcRoleFieldRequired?.[0]?.codename === 'yes' ||
		field.elements.formCdcBusinessSearchFieldRequired?.[0]?.codename ===
			'yes' ||
		field.elements.formCdcSalutationFieldRequired?.[0]?.codename ===
			'yes' ||
		field.elements.formCdcTelephoneFieldRequired?.[0]?.codename === 'yes' ||
		field.elements.formCdcBusinessTelephoneFieldRequired?.[0]?.codename ===
			'yes' ||
		field.elements.formCdcBusinessNameFieldRequired?.[0]?.codename ===
			'yes' ||
		field.elements.formCdcProfessionalNumberFieldRequired?.[0]?.codename ===
			'yes' ||
		field.elements.formCdcBusinessTypeFieldRequired?.[0]?.codename ===
			'yes' ||
		field.elements.formCdcExplicitConsentCheckboxFieldRequired?.[0]
			?.codename === 'yes' ||
		// Non-CDC Fields
		field.elements.formCheckboxFieldRequired?.[0]?.codename === 'yes' ||
		field.elements.formCheckboxGroupFieldRequired?.[0]?.codename ===
			'yes' ||
		field.elements.formComboboxFieldRequired?.[0]?.codename === 'yes' ||
		field.elements.formRadioGroupFieldRequired?.[0]?.codename === 'yes' ||
		field.elements.formSelectFieldRequired?.[0]?.codename === 'yes' ||
		field.elements.formTextFieldRequired?.[0]?.codename === 'yes' ||
		field.elements.formNumberFieldRequired?.[0]?.codename === 'yes' ||
		field.elements.formTelephoneFieldRequired?.[0]?.codename === 'yes' ||
		field.elements.formSwitchFieldRequired?.[0]?.codename === 'yes'
	)
}

/**
 * Create schema for CDC email field
 * Note: Email field is always required by design (no CMS option to make it optional)
 */
const createEmailFieldSchema = (locale: LocaleKey): z.ZodTypeAny => {
	return z
		.string()
		.min(1, getFieldError(locale, 'email', 'required'))
		.email(getFieldError(locale, 'email', 'invalidEmail'))
}

/**
 * Create schema for CDC first name field
 */
const createFirstNameFieldSchema = (
	locale: LocaleKey,
	isRequired: boolean
): z.ZodTypeAny => {
	return z
		.string()
		.max(50, getFieldError(locale, 'firstName', 'maxLength'))
		.refine((val) => !isRequired || val.length >= 1, {
			message: getFieldError(locale, 'firstName', 'required'),
		})
		.refine((val) => val.length === 0 || val.length >= 2, {
			message: getFieldError(locale, 'firstName', 'minLength'),
		})
}

/**
 * Create schema for CDC last name field
 */
const createLastNameFieldSchema = (
	locale: LocaleKey,
	isRequired: boolean
): z.ZodTypeAny => {
	return z
		.string()
		.max(50, getFieldError(locale, 'lastName', 'maxLength'))
		.refine((val) => !isRequired || val.length >= 1, {
			message: getFieldError(locale, 'lastName', 'required'),
		})
		.refine((val) => val.length === 0 || val.length >= 2, {
			message: getFieldError(locale, 'lastName', 'minLength'),
		})
}

/**
 * Create schema for CDC business name field
 */
const createBusinessNameFieldSchema = (
	locale: LocaleKey,
	isRequired: boolean
): z.ZodTypeAny => {
	return z
		.string()
		.max(100, getFieldError(locale, 'businessName', 'maxLength'))
		.refine((val) => !isRequired || val.length >= 1, {
			message: getFieldError(locale, 'businessName', 'required'),
		})
		.refine((val) => val.length === 0 || val.length >= 2, {
			message: getFieldError(locale, 'businessName', 'minLength'),
		})
}

/**
 * Create schema for CDC role field
 */
const createRoleFieldSchema = (
	field: FormField,
	locale: LocaleKey,
	isRequired: boolean
): z.ZodTypeAny => {
	const allowedRoleValues = getConfiguredRoleValues(field)

	return z
		.string()
		.refine(
			(val) =>
				(val.length === 0 && !isRequired) ||
				allowedRoleValues.includes(val),
			{
				message: getFieldError(locale, 'role', 'invalid'),
			}
		)
}

/**
 * Create schema for CDC business search field
 */
const createBusinessSearchFieldSchema = (
	locale: LocaleKey,
	isRequired: boolean
): z.ZodTypeAny => {
	if (isRequired) {
		return z.object({
			_validation: z
				.string()
				.min(
					1,
					getFieldError(locale, 'businessSearchField', 'required')
				),
			entryMethod: z.string().optional(),
			business: z.object({
				businessType: z
					.string()
					.min(1, getFieldError(locale, 'businessType', 'required')),
				name: z
					.string()
					.min(1, getFieldError(locale, 'businessName', 'required')),
				line1: z
					.string()
					.min(1, getFieldError(locale, 'addressLine1', 'required')),
				line2: z.string().optional(),
				city: z
					.string()
					.min(1, getFieldError(locale, 'city', 'required')),
				state: z
					.string()
					.min(1, getFieldError(locale, 'state', 'required')),
				postalCode: z
					.string()
					.min(1, getFieldError(locale, 'zipCode', 'required')),
				CRMId: z.string().optional(),
			}),
		})
	}

	return z.object({
		_validation: z.string().optional(),
		entryMethod: z.string().optional(),
		business: z
			.object({
				businessType: z.string().optional(),
				name: z.string().optional(),
				line1: z.string().optional(),
				line2: z.string().optional(),
				city: z.string().optional(),
				state: z.string().optional(),
				postalCode: z.string().optional(),
				CRMId: z.string().optional(),
			})
			.optional(),
	})
}

/**
 * Create schema for CDC explicit consent field
 */
const createExplicitConsentFieldSchema = (
	locale: LocaleKey,
	isRequired: boolean
): z.ZodTypeAny => {
	return isRequired
		? z.boolean().refine((val) => val, {
				message: getFieldError(locale, 'explicitConsent', 'required'),
			})
		: z.boolean()
}

/**
 * Create schema for CDC implicit consent field
 */
const createImplicitConsentFieldSchema = (): z.ZodTypeAny => {
	return z.literal(true)
}

/**
 * Create schema for CDC salutation field
 */
const createSalutationFieldSchema = (
	locale: LocaleKey,
	isRequired: boolean
): z.ZodTypeAny => {
	return isRequired
		? z.string().min(1, getFieldError(locale, 'salutation', 'required'))
		: z.string().optional()
}

/**
 * Create schema for CDC telephone field
 */
const createTelephoneFieldSchema = (
	locale: LocaleKey,
	isRequired: boolean
): z.ZodTypeAny => {
	const baseSchema = z.object({
		diallingCode: z.string(),
		localNumber: z.string(),
		country: z.string().optional(),
	})

	if (isRequired) {
		return baseSchema
			.refine(
				(data) => {
					const hasDiallingCode =
						data.diallingCode && data.diallingCode.length > 0
					const hasLocalNumber =
						data.localNumber && data.localNumber.length > 0
					return hasDiallingCode && hasLocalNumber
				},
				{
					message: getFieldError(
						locale,
						'telephoneField',
						'invalidTelephone'
					),
					path: [],
				}
			)
			.refine(
				(data) => {
					if (data.localNumber && data.localNumber.length > 0) {
						return /^\d+$/.test(data.localNumber)
					}
					return true
				},
				{
					message: getFieldError(
						locale,
						'telephoneField',
						'invalidFormat'
					),
					path: [],
				}
			)
	}

	return baseSchema.refine(
		(data) => {
			if (!data.localNumber || data.localNumber.length === 0) {
				return true
			}
			return /^\d+$/.test(data.localNumber)
		},
		{
			message: getFieldError(locale, 'telephoneField', 'invalidFormat'),
			path: [],
		}
	)
}

/**
 * Create schema for text field
 */
const createTextFieldSchema = (
	locale: LocaleKey,
	isRequired: boolean
): z.ZodTypeAny => {
	return z
		.string()
		.max(255, getFieldError(locale, 'text', 'maxLength'))
		.refine((val) => !isRequired || val.length >= 1, {
			message: getFieldError(locale, 'text', 'required'),
		})
		.refine((val) => val.length === 0 || val.length >= 2, {
			message: getFieldError(locale, 'text', 'minLength'),
		})
}

/**
 * Create schema for combobox field
 */
const createComboboxFieldSchema = (
	locale: LocaleKey,
	isRequired: boolean
): z.ZodTypeAny => {
	return z.string().refine((val) => !isRequired || val.length >= 1, {
		message: getFieldError(locale, 'combobox', 'required'),
	})
}

/**
 * Create schema for number field
 */
const createNumberFieldSchema = (
	field: FormField,
	locale: LocaleKey,
	isRequired: boolean
): z.ZodTypeAny => {
	return z
		.number()
		.nullable()
		.refine((val) => !isRequired || val !== null, {
			message: getFieldError(locale, 'number', 'required'),
		})
		.refine(
			(val) =>
				val === null ||
				(field.elements?.formNumberFieldMin
					? val >= field.elements.formNumberFieldMin
					: true),
			{message: getFieldError(locale, 'number', 'minValue')}
		)
		.refine(
			(val) =>
				val === null ||
				(field.elements?.formNumberFieldMax
					? val <= field.elements.formNumberFieldMax
					: true),
			{message: getFieldError(locale, 'number', 'maxValue')}
		)
}

/**
 * Create schema for checkbox group field
 */
const createCheckboxGroupFieldSchema = (
	locale: LocaleKey,
	isRequired: boolean
): z.ZodTypeAny => {
	return z.array(z.string()).refine((val) => !isRequired || val.length >= 1, {
		message: getFieldError(locale, 'checkboxGroup', 'required'),
	})
}

/**
 * Create schema for radio group field
 */
const createRadioGroupFieldSchema = (
	locale: LocaleKey,
	isRequired: boolean
): z.ZodTypeAny => {
	return z.string().refine((val) => !isRequired || val.length >= 1, {
		message: getFieldError(locale, 'checkboxGroup', 'required'),
	})
}

/**
 * Create schema for select field
 */
const createSelectFieldSchema = (
	locale: LocaleKey,
	isRequired: boolean
): z.ZodTypeAny => {
	return z.string().refine((val) => !isRequired || val.length >= 1, {
		message: getFieldError(locale, 'select', 'required'),
	})
}

/**
 * Create schema for checkbox field
 */
const createCheckboxFieldSchema = (
	locale: LocaleKey,
	isRequired: boolean
): z.ZodTypeAny => {
	return isRequired
		? z.boolean().refine((val) => val, {
				message: getFieldError(locale, 'checkbox', 'required'),
			})
		: z.boolean()
}

/**
 * Get field schema based on field type
 */
const getFieldSchema = (
	field: FormField,
	locale: LocaleKey,
	isRequired: boolean
): z.ZodTypeAny => {
	const fieldType = field.system.type

	switch (fieldType) {
		case 'form_cdc_email_field': {
			if (
				field.elements?.formCdcEmailFieldRequireConfirmation?.[0]
					?.codename === 'yes'
			) {
				return z.string()
			}
			return createEmailFieldSchema(locale)
		}
		case 'form_cdc_first_name_field':
			return createFirstNameFieldSchema(locale, isRequired)
		case 'form_cdc_last_name_field':
			return createLastNameFieldSchema(locale, isRequired)
		case 'form_cdc_business_name_field':
			return createBusinessNameFieldSchema(locale, isRequired)
		case 'form_cdc_role_field':
			return createRoleFieldSchema(field, locale, isRequired)
		case 'form_cdc_business_search_field':
			return createBusinessSearchFieldSchema(locale, isRequired)
		case 'form_cdc_explicit_consent_field':
			return createExplicitConsentFieldSchema(locale, isRequired)
		case 'form_cdc_implicit_consent_field':
			return createImplicitConsentFieldSchema()
		case 'form_cdc_salutation_field':
			return createSalutationFieldSchema(locale, isRequired)
		case 'form_cdc_telephone_field':
			return createTelephoneFieldSchema(locale, isRequired)
		case 'form_cdc_business_telephone_field':
			return createTelephoneFieldSchema(locale, isRequired)
		case 'form_checkbox_group_field':
			return createCheckboxGroupFieldSchema(locale, isRequired)
		case 'form_radio_group_field':
			return createRadioGroupFieldSchema(locale, isRequired)
		case 'form_select_field':
			return createSelectFieldSchema(locale, isRequired)
		case 'form_combobox_field':
			return createComboboxFieldSchema(locale, isRequired)
		case 'form_checkbox_field':
			return createCheckboxFieldSchema(locale, isRequired)
		case 'form_text_field':
			return createTextFieldSchema(locale, isRequired)
		case 'form_number_field':
			return createNumberFieldSchema(field, locale, isRequired)
		default:
			throw new Error(`Unsupported field type: ${fieldType}`)
	}
}

/**
 * Generate Zod schema dynamically based on form questions
 */
export const generateFormSchema = (
	questions: FormField[],
	locale: LocaleKey
): FormGenerationResult<z.ZodObject<Record<string, z.ZodTypeAny>>> => {
	const schemaObj: Record<string, z.ZodTypeAny> = {}

	// Handle empty questions array
	if (!Array.isArray(questions) || questions.length === 0) {
		return {
			data: z.object({}),
		}
	}

	for (const field of questions) {
		// Skip invalid field objects
		if (!field.system.type || !field.system.codename) {
			continue
		}

		const fieldType = field.system.type
		const origFieldName = field.system.name

		// Generate field name from type
		let fieldName = null
		if (
			fieldType === 'form_cdc_email_field' &&
			field.elements?.formCdcEmailFieldRequireConfirmation?.[0]
				?.codename === 'yes'
		) {
			fieldName = 'confirmEmail'
		} else {
			fieldName = convertTypeToFieldName(fieldType) ?? ''
		}

		// Skip buttons and display text as they don't need schema validation
		if (
			!fieldName ||
			fieldType === 'form_button' ||
			fieldType === 'form_display_text'
		) {
			continue
		}

		// Check if field is required
		const isRequired = isFieldRequired(field)

		// Get field schema using helper function
		let fieldSchema: z.ZodTypeAny
		try {
			fieldSchema = getFieldSchema(field, locale, isRequired)
		} catch (error) {
			// Log error for monitoring and Azure alerting
			const schemaError = new Error(
				`Unsupported field type in form schema: ${fieldType}. Field codename: ${origFieldName}. Field ID: ${field.system.id}`
			)
			logError(schemaError)
			// Return error instead of throwing
			return {
				data: z.object({}),
				error: {
					message: 'Unsupported field',
					fieldType,
					fieldCodename: origFieldName,
					fieldId: field.system.id,
				},
			}
		}

		schemaObj[fieldName] = fieldSchema

		// Auto-inject professionalNumber schema when role field has roles requiring it
		if (
			fieldType === 'form_cdc_role_field' &&
			(field.elements?.formCdcRoleFieldRolesRequiringProfessionalNumber
				?.length ?? 0) > 0 &&
			!('professionalNumber' in schemaObj)
		) {
			schemaObj.professionalNumber = z
				.object({
					number: z
						.string()
						.min(
							1,
							getFieldError(
								locale,
								'professionalNumber',
								'required'
							)
						),
					state: z
						.string()
						.min(
							1,
							getFieldError(locale, 'jurisdiction', 'required')
						),
				})
				.optional()
		}
	}

	let formSchema = z.object(schemaObj)

	// Add email confirmation validation if both email and confirmEmail fields exist
	if ('email' in schemaObj && 'confirmEmail' in schemaObj) {
		formSchema = formSchema.refine(
			(data) => {
				return data.email === data.confirmEmail
			},
			{
				message: getFieldError(
					locale,
					'confirmEmail',
					'emailMatchError'
				),
				path: ['confirmEmail'],
			}
		) as z.ZodObject
	}

	return {
		data: formSchema,
	}
}

/**
 * Generate default values for the form based on questions
 */
export const generateDefaultValues = (
	questions: FormField[]
): FormGenerationResult<
	Record<string, string | string[] | boolean | object | null>
> => {
	const defaults: Record<
		string,
		string | string[] | boolean | object | null
	> = {}

	if (questions.length === 0) {
		return {
			data: defaults,
		}
	}

	for (const field of questions) {
		// Skip empty or malformed fields
		if (!field.system.type || !field.system.codename) {
			continue
		}
		const fieldType = field.system.type
		const origFieldName = field.system.name

		// Convert field codename to camelCase for form field names
		let fieldName = null
		if (
			fieldType === 'form_cdc_email_field' &&
			field.elements?.formCdcEmailFieldRequireConfirmation?.[0]
				?.codename === 'yes'
		) {
			fieldName = 'confirmEmail'
		} else {
			fieldName = convertTypeToFieldName(fieldType) ?? ''
		}

		// Skip fields without a valid name mapping
		if (!fieldName) {
			continue
		}

		// Set default values based on field type
		switch (fieldType) {
			case 'form_cdc_email_field':
			case 'form_cdc_first_name_field':
			case 'form_cdc_last_name_field':
			case 'form_cdc_role_field':
			case 'form_cdc_business_name_field':
			case 'form_cdc_salutation_field':
			case 'form_radio_group_field':
			case 'form_select_field':
			case 'form_combobox_field':
			case 'form_text_field':
				defaults[fieldName] = ''
				break
			case 'form_cdc_business_search_field':
				defaults[fieldName] = {
					_validation: '',
					entryMethod: 'automatic',
					business: {
						businessType: '',
						name: '',
						line1: '',
						line2: '',
						city: '',
						state: '',
						postalCode: '',
						CRMId: '',
					},
				}
				break
			case 'form_cdc_explicit_consent_field':
			case 'form_checkbox_field':
				defaults[fieldName] = false
				break
			case 'form_cdc_implicit_consent_field':
				defaults[fieldName] = true
				break
			case 'form_cdc_telephone_field':
			case 'form_cdc_business_telephone_field':
				defaults[fieldName] = {
					diallingCode: '',
					localNumber: '',
				}
				break
			case 'form_checkbox_group_field':
				defaults[fieldName] = []
				break
			case 'form_number_field':
				defaults[fieldName] = null
				break
			case 'form_button':
			case 'form_display_text':
				break
			default: {
				// Log error for monitoring and Azure alerting
				const defaultsError = new Error(
					`Unsupported field type in default values: ${fieldType}. Field codename: ${origFieldName}. Field ID: ${field.system.id}`
				)
				logError(defaultsError)
				// Return error instead of throwing
				return {
					data: {},
					error: {
						message: 'Unsupported field',
						fieldType,
						fieldCodename: origFieldName,
						fieldId: field.system.id,
					},
				}
			}
		}

		// Auto-inject professionalNumber default when role field has roles requiring it
		if (
			fieldType === 'form_cdc_role_field' &&
			(field.elements?.formCdcRoleFieldRolesRequiringProfessionalNumber
				?.length ?? 0) > 0 &&
			!('professionalNumber' in defaults)
		) {
			defaults.professionalNumber = {
				number: '',
				state: '',
			}
		}
	}

	return {
		data: defaults,
	}
}

export const LOCALE_MAPPING: Record<string, string> = {
	en_gb: 'en-GB',
	us: 'en-US',
	au: 'en-AU',
	nl: 'nl-NL',
	dk: 'da-DK',
	se: 'sv-SE',
	ru: 'ru-RU',
	it: 'it-IT',
	en_ca: 'en-CA',
	fr_ca: 'fr-CA',
	fr: 'fr-FR',
	hu: 'hu-HU',
	de: 'de-DE',
	br: 'pt-BR',
	es: 'es-ES',
	mx: 'es-MX',
	pl: 'pl-PL',
	ar: 'es-AR',
	at: 'de-AT',
	nl_be: 'nl-BE',
	fr_be: 'fr-BE',
	cn: 'zh-CN',
	cl: 'es-ES',
	co: 'es-ES',
	cz: 'cs-CZ',
	jp: 'ja-JP',
	pt: 'pt-PT',
	ua: 'ru-UA',
	nz: 'en-GB',
	za: 'en-ZA',
	th: 'th-TH',
	fi: 'fi-FI',
	no: 'no-NO',
	tr: 'tr-TR',
	hr: 'hr-HR',
	ro: 'ro-RO',
	si: 'sl-SI',
	bg: 'bg-BG',
	ie: 'en-IE',
	fr_lu: 'fr-LU',
	de_lu: 'de-LU',
	it_mt: 'it-MT',
	el_cy: 'el-CY',
	de_be: 'de-BE',
	et: 'et-EE',
	sk: 'sk-SK',
	lv: 'lv-LV',
	lt: 'lt-LT',
	gr: 'el-GR',
	is: 'is-IS',
	vn: 'vi-VN',
	id: 'id-ID',
	in: 'en-IN',
	kr: 'ko-KR',
	my: 'ms-MY',
}

const FIELD_NAME_MAPPING: Record<string, string> = {
	form_cdc_first_name_field: 'firstName',
	form_cdc_last_name_field: 'lastName',
	form_cdc_email_field: 'email',
	form_cdc_role_field: 'role',
	form_cdc_business_search_field: 'businessSearch',
	form_cdc_salutation_field: 'salutation',
	form_cdc_telephone_field: 'telephone',
	form_cdc_business_telephone_field: 'businessTelephone',
	form_cdc_business_name_field: 'businessName',
	form_cdc_explicit_consent_field: 'explicitConsent',
	form_cdc_implicit_consent_field: 'implicitConsent',
	form_cdc_address_city_field: 'addressCity',
	form_cdc_address_line1_field: 'addressLine1',
	form_cdc_address_line2_field: 'addressLine2',
	form_cdc_address_zip_code_field: 'addressPostalCode',
	form_cdc_address_state_field: 'addressState',
	form_checkbox_group_field: 'checkboxGroup',
	form_radio_group_field: 'radioGroup',
	form_select_field: 'select',
	form_checkbox_field: 'checkbox',
	form_combobox_field: 'combobox',
	form_text_field: 'text',
	form_number_field: 'number',
	form_address_search_field: 'addressSearch',
	form_display_text: 'displayText',
	form_button: 'button',
}

export const extractCountryCode = (code: string): string => {
	if (!(code in LOCALE_MAPPING)) return 'US'
	const parts = code.split('_')
	return (parts[1] || parts[0] || 'US').toUpperCase()
}
